<template>
  <div
    class="yard-view-spot-select-wrapper d-flex flex-column gap-2"
    data-testid="yard-view-spot-select-wrapper">
    <v-autocomplete
      data-testid="yard-view-spot-select"
      color="primary"
      density="compact"
      item-title="code"
      item-value="id"
      variant="outlined"
      :clearable="clearable"
      :disabled="disabled"
      :hide-details="hideDetails"
      :hint="hint"
      :items="spotsByArea"
      :model-value="modelValue"
      :multiple="multiple"
      :persistent-hint="persistentHint"
      :placeholder="placeholder"
      :required="required"
      :rules="rules"
      @update:model-value="handleValueChange">
      <template #no-data>
        <v-list-item :title="t('yard.messages.noSpotsAvailable')"></v-list-item>
      </template>

      <template #selection="{ index }">
        <span v-if="index === 0" class="typography-body-md">
          {{ getRenderedSelectedValue() }}
        </span>
      </template>

      <template #item="{ item, props }">
        <v-list-subheader
          v-if="item.raw.header"
          :title="item.raw.header"
          class="typography-caption-bold"></v-list-subheader>

        <v-list-item v-else v-bind="props" :disabled="item.raw.disabled">
          <template #prepend>
            <v-icon size="x-small">{{ item.raw.icon }}</v-icon>
          </template>

          <template #title>
            <div class="d-flex flex-column justify-center">
              <span class="typography-body-md">{{ item.title }}</span>
              <span class="typography-caption">{{ item.raw.group }}</span>
            </div>
          </template>

          <template #append v-if="showSpotStatus">
            <span class="typography-body-sm text-capitalize text--color-text-secondary">
              {{ item.raw.status }}
            </span>
          </template>
        </v-list-item>
      </template>

      <template #message="{ message }">
        <small class="typography-body-sm">
          {{ message }}
        </small>
      </template>
    </v-autocomplete>

    <!-- slot for any additional content -->
    <slot
      v-if="modelValue && selectedSpot"
      name="selected-spot"
      :selected-spot="selectedSpot"></slot>
  </div>
</template>

<script>
import { str as StrLib } from '@nova/core';
import { computed, defineComponent } from 'vue';
import useYard from '@/modules/yard/composables/useYard';
import { useI18n } from 'vue-i18n';

export default defineComponent({
  name: 'YardViewSpotSelect',
  emits: ['update:modelValue', 'input'],
  props: {
    modelValue: {
      type: [String, Number],
      default: null
    },
    includeClosedSpots: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    includeAssignedSpots: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    includeReservedSpots: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    includeAssociatedToActiveMoveTasks: {
      type: [Boolean, String],
      default: false,
      validator: value => {
        if (['boolean', 'undefined'].includes(typeof value)) {
          return true;
        }

        return value === 'disabled';
      }
    },
    /**
     * This is used to determine what type of asset you want to select a spot for.
     * If you are selecting a spot for an assetVisit, you can select an
     * spot with an assetContainer.
     * If you are selecting a spot for an assetContainer, you can select an assetVisit.
     */
    assetType: {
      type: String,
      validator: value => {
        if (typeof value === 'string') {
          return ['assetVisit', 'assetContainer'].includes(value);
        }

        return typeof value === 'undefined';
      }
    },
    showSpotStatus: {
      type: Boolean,
      default: false
    },
    clearable: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    hideDetails: {
      type: Boolean,
      default: false
    },
    hint: {
      type: String,
      default: ''
    },
    multiple: {
      type: Boolean,
      default: false
    },
    persistentHint: {
      type: Boolean,
      default: false
    },
    placeholder: {
      type: String,
      default: 'Select a spot'
    },
    required: {
      type: Boolean,
      default: false
    },
    rules: {
      type: Array,
      default: () => []
    }
  },
  setup(props, { emit }) {
    const yardStore = useYard();
    const { t } = useI18n();

    const handleValueChange = newValue => {
      emit('update:modelValue', newValue);
      emit('input', newValue);
    };

    const shouldExcludeClosedSpot = spot => {
      return yardStore.isSpotClosed(spot);
    };

    const shouldExcludeReservedSpot = spot => {
      return yardStore.isSpotReserved(spot);
    };

    const shouldExcludeAssignedSpot = spot => {
      return yardStore.isSpotAssigned(spot);
    };

    const shouldExcludeSpotAssociatedToActiveMoveTask = spot => {
      return yardStore.isSpotInActiveMoveTask(spot.id);
    };

    const shouldDisableByAssetType = spot => {
      const assetType = props.assetType;

      if (!assetType || yardStore.isSpotOpen(spot)) {
        return false;
      }

      if (assetType === 'assetVisit') {
        return !yardStore.hasAssetContainerOnSpot(spot) || yardStore.hasAssetVisitOnSpot(spot);
      }

      if (assetType === 'assetContainer') {
        return yardStore.hasAssetContainerOnSpot(spot);
      }

      return false;
    };

    const shouldDisableSpot = spot => {
      return (
        (props.includeReservedSpots === 'disabled' && yardStore.isSpotReserved(spot)) ||
        (props.includeClosedSpots === 'disabled' && yardStore.isSpotClosed(spot)) ||
        (props.includeAssignedSpots === 'disabled' && yardStore.isSpotAssigned(spot)) ||
        (props.includeAssociatedToActiveMoveTasks === 'disabled' &&
          yardStore.isSpotInActiveMoveTask(spot.id)) ||
        shouldDisableByAssetType(spot)
      );
    };

    const spotsByArea = computed(() => {
      const allSpots = yardStore.spotsRef.value || [];
      const areas = yardStore.spotAreasRef.value || [];

      const areasMap = new Map();
      areas.forEach(area => {
        areasMap.set(area.id, {
          area,
          spots: []
        });
      });

      const exclusions = [
        ...(props.includeClosedSpots ? [] : [shouldExcludeClosedSpot]),
        ...(props.includeReservedSpots ? [] : [shouldExcludeReservedSpot]),
        ...(props.includeAssignedSpots ? [] : [shouldExcludeAssignedSpot]),
        ...(props.includeAssociatedToActiveMoveTasks
          ? []
          : [shouldExcludeSpotAssociatedToActiveMoveTask])
      ];
      const isExcludedBySomeFilter = spot => exclusions.some(exclude => exclude(spot));

      allSpots.forEach(spot => {
        const areaData = areasMap.get(spot.spotAreaId);

        if (!areaData || isExcludedBySomeFilter(spot)) {
          return;
        }

        areaData.spots.push({
          code: spot.code,
          id: spot.id,
          icon: yardStore.getSpotIcon(spot.type),
          group: areaData.area.name,
          status: yardStore.getTranslatedSpotStatus(spot),
          disabled: shouldDisableSpot(spot)
        });
      });

      const items = [];
      areas.forEach(area => {
        const areaData = areasMap.get(area.id);

        if (areaData && areaData.spots.length > 0) {
          // Sort spots within the area
          areaData.spots.sort((a, b) => StrLib.compareAlphaNumeric(a.code, b.code));

          items.push({ header: area.name.toUpperCase() });
          items.push(...areaData.spots);
        }
      });

      return items;
    });

    const selectedSpot = computed(() => {
      if (!props.modelValue) {
        return null;
      }

      const selectedSpot = spotsByArea.value.find(spot => spot.id === props.modelValue);
      return selectedSpot ?? null;
    });

    const getRenderedSelectedValue = () => {
      return selectedSpot.value ? `${selectedSpot.value.group} - ${selectedSpot.value.code}` : '';
    };

    return {
      t,
      spotsByArea,
      handleValueChange,
      getRenderedSelectedValue,
      selectedSpot
    };
  }
});
</script>
